/**
 * mirrorLink — carry a performance to a second display.
 *
 * We do NOT mirror pixels. A Glyph frame is a pure function of the shader
 * uniforms (one quad, one draw call, no render targets, no feedback buffers), and
 * every one of those uniforms is a plain number on the paramBus. So the mirror
 * runs its own Visualizer off its own bus, and all that crosses the wire is
 * `{k, v}` per change — sub-millisecond over BroadcastChannel, no server, and
 * each display renders at its own resolution and refresh rate. Streaming
 * `canvas.captureStream()` instead would cost 50–150ms of encode/decode and
 * letterbox the output; this costs nothing.
 *
 * The link keeps the one-way flow intact (see CLAUDE.md non-negotiable #2): the
 * receiver applies everything through `bus.set()`, exactly as real input does, and
 * the mirror page has no input layer at all, so nothing else ever writes to it.
 *
 * Two things need care, and both are handled here so nothing outside this file
 * has to think about them:
 *
 *   1. `virtualTime` is the only visual state that isn't on the bus (it's
 *      integrated per-frame in the Visualizer). Without it the mirror renders the
 *      right *look* at the wrong *moment*, so it travels in `clock` messages and
 *      the Visualizer eases onto it.
 *   2. `tempo.anchorMs` is a `performance.now()` value, and `performance.now()` has
 *      a **per-document time origin**. Shipped raw, the mirror's beat phase would
 *      be wrong by the origin delta (visibly off-beat on Strobe). It travels as an
 *      epoch instead — this module is the only place that conversion happens.
 */

import { ParamBus } from '../state/paramBus';

/** BroadcastChannel name shared by the controller page and the mirror page. */
export const MIRROR_CHANNEL = 'glyph-mirror';

/** The one param that can't cross a document boundary raw (see header, note 2). */
export const TEMPO_ANCHOR = 'tempo.anchorMs';

/** Default gap between `clock` messages. Drift correction only; taps are instant. */
const CLOCK_INTERVAL_MS = 250;

/** Silence after which the receiver considers the controller gone. */
const LINK_TIMEOUT_MS = 3000;

export type MirrorMessage =
  /** Controller announcing itself, so a mirror that loaded first can ask for state. */
  | { readonly t: 'hello' }
  /** Mirror asking for a full snapshot. */
  | { readonly t: 'req' }
  /** Full state: every param (minus the anchor) plus both clocks. */
  | {
      readonly t: 'snap';
      readonly params: Record<string, number>;
      readonly vt: number;
      readonly anchorEpoch: number;
    }
  /** One param change. */
  | { readonly t: 'p'; readonly k: string; readonly v: number }
  /** Clock heartbeat: virtual time + the beat anchor as an epoch. */
  | { readonly t: 'clock'; readonly vt: number; readonly anchorEpoch: number }
  /** Either end going away. */
  | { readonly t: 'bye' };

/**
 * The slice of BroadcastChannel the link uses. Injectable so the unit tests can
 * run a sender and a receiver against an in-memory pair, and so a cross-device
 * WebSocket transport can drop in later without touching either end.
 */
export interface MirrorPort {
  postMessage(message: MirrorMessage): void;
  onmessage: ((event: { data: MirrorMessage }) => void) | null;
  close(): void;
}

/**
 * The slice of Visualizer the link reads. Keeps the transport out of the scene's
 * types — mirrorLink never imports Three.js.
 */
export interface MirrorClock {
  /** The virtual animation clock (`uTime`), integrated per-frame. */
  readonly virtualTime: number;
  /** The anchor `beatEnvelope()` is actually using, in local `performance.now()` ms. */
  readonly beatAnchor: number;
  /** Adopt the controller's virtual time (eased, not snapped). */
  syncVirtualTime(vt: number): void;
}

function defaultOrigin(): number {
  return typeof performance !== 'undefined' && typeof performance.timeOrigin === 'number'
    ? performance.timeOrigin
    : 0;
}

/**
 * Local `performance.now()` ms -> epoch ms.
 *
 * 0 passes through untouched: the Visualizer reads a non-positive anchor as "no
 * tempo set yet" (`beatEnvelope` guards on `> 0`), so it must not become a
 * time-origin-sized number on the way across.
 */
export function toEpoch(perfMs: number, origin: number = defaultOrigin()): number {
  return perfMs > 0 ? perfMs + origin : 0;
}

/** Epoch ms -> local `performance.now()` ms. Inverse of {@link toEpoch}. */
export function fromEpoch(epochMs: number, origin: number = defaultOrigin()): number {
  return epochMs > 0 ? epochMs - origin : 0;
}

/**
 * Apply a full snapshot to a bus.
 *
 * Keys the bus knows but the snapshot doesn't mention are zeroed first. Only
 * `fx.*` / `latch.*` can be absent (they materialize on first press, while
 * `mod.*` / `vmod.*` / selectors are all seeded at boot), and 0 is their correct
 * default — so this is what stops a latched effect from being stranded on the
 * mirror when the controller page reloads mid-set.
 */
export function applySnapshot(bus: ParamBus, params: Record<string, number>): void {
  for (const key of Object.keys(bus.snapshot())) {
    if (key !== TEMPO_ANCHOR && !(key in params)) bus.set(key, 0);
  }
  for (const [key, value] of Object.entries(params)) bus.set(key, value);
}

function openChannel(): MirrorPort {
  // BroadcastChannel hands its handler a full MessageEvent; MirrorPort only ever
  // reads `.data`, which is the narrower contract a test double can satisfy.
  return new BroadcastChannel(MIRROR_CHANNEL) as unknown as MirrorPort;
}

/** Register a page-teardown hook where one exists (no-op under Node/tests). */
function onPageHide(fn: () => void): () => void {
  if (typeof window === 'undefined') return () => {};
  window.addEventListener('pagehide', fn);
  return () => window.removeEventListener('pagehide', fn);
}

export interface MirrorSenderOptions {
  port?: MirrorPort;
  /** ms between clock heartbeats. Default 250. */
  clockIntervalMs?: number;
  /** Time origin to convert against. Injectable for tests. */
  origin?: number;
  /** A mirror asked for state — the link is live. */
  onReceiver?: () => void;
  /** A mirror said goodbye. */
  onReceiverGone?: () => void;
}

/**
 * Publish this page's performance. Cheap and safe with nothing listening: a
 * BroadcastChannel post with no other window open is a no-op, so `/prototypes/glyph/`
 * behaves exactly as it did before whether or not a mirror is running.
 *
 * Returns a detach function.
 */
export function attachMirrorSender(
  bus: ParamBus,
  clock: MirrorClock,
  options: MirrorSenderOptions = {},
): () => void {
  const port = options.port ?? openChannel();
  const origin = options.origin ?? defaultOrigin();

  const clockMessage = (anchorPerfMs: number = clock.beatAnchor): MirrorMessage => ({
    t: 'clock',
    vt: clock.virtualTime,
    anchorEpoch: toEpoch(anchorPerfMs, origin),
  });

  const unsubscribe = bus.subscribe((change) => {
    // The anchor travels as an epoch inside `clock`, never as a raw param. Sent
    // the moment it changes (rather than on the next heartbeat) so a Space tap
    // lands on the mirror in time. Reading `change.value` rather than
    // `clock.beatAnchor` keeps this independent of subscriber ordering.
    if (change.key === TEMPO_ANCHOR) {
      port.postMessage(clockMessage(change.value));
      return;
    }
    port.postMessage({ t: 'p', k: change.key, v: change.value });
  });

  port.onmessage = (event) => {
    const message = event.data;
    if (message.t === 'req') {
      const params = bus.snapshot();
      delete params[TEMPO_ANCHOR];
      port.postMessage({
        t: 'snap',
        params,
        vt: clock.virtualTime,
        anchorEpoch: toEpoch(clock.beatAnchor, origin),
      });
      options.onReceiver?.();
    } else if (message.t === 'bye') {
      options.onReceiverGone?.();
    }
  };

  const sayBye = (): void => port.postMessage({ t: 'bye' });
  const offPageHide = onPageHide(sayBye);
  port.postMessage({ t: 'hello' });
  const timer = setInterval(
    () => port.postMessage(clockMessage()),
    options.clockIntervalMs ?? CLOCK_INTERVAL_MS,
  );

  return () => {
    clearInterval(timer);
    unsubscribe();
    offPageHide();
    sayBye();
    port.onmessage = null;
    port.close();
  };
}

export interface MirrorReceiverOptions {
  port?: MirrorPort;
  /** Time origin to convert against. Injectable for tests. */
  origin?: number;
  /** ms of silence before the controller counts as gone. Default 3000. */
  timeoutMs?: number;
  onConnect?: () => void;
  onDisconnect?: () => void;
}

/**
 * Drive a bus (and its Visualizer's clock) from a controller page.
 *
 * Handles both cold-load orders: it asks for a snapshot on attach (controller
 * already running) and answers a `hello` with the same request (controller
 * started later). Either way the mirror comes up already matching, mid-set.
 *
 * Returns a detach function.
 */
export function attachMirrorReceiver(
  bus: ParamBus,
  clock: MirrorClock,
  options: MirrorReceiverOptions = {},
): () => void {
  const port = options.port ?? openChannel();
  const origin = options.origin ?? defaultOrigin();
  const timeoutMs = options.timeoutMs ?? LINK_TIMEOUT_MS;
  let connected = false;
  let idleTimer: ReturnType<typeof setTimeout> | undefined;

  const drop = (): void => {
    if (!connected) return;
    connected = false;
    options.onDisconnect?.();
  };

  const markAlive = (): void => {
    if (!connected) {
      connected = true;
      options.onConnect?.();
    }
    clearTimeout(idleTimer);
    idleTimer = setTimeout(drop, timeoutMs);
  };

  const applyClock = (vt: number, anchorEpoch: number): void => {
    bus.set(TEMPO_ANCHOR, fromEpoch(anchorEpoch, origin));
    clock.syncVirtualTime(vt);
  };

  port.onmessage = (event) => {
    const message = event.data;
    switch (message.t) {
      case 'hello':
        port.postMessage({ t: 'req' });
        markAlive();
        break;
      case 'snap':
        applySnapshot(bus, message.params);
        applyClock(message.vt, message.anchorEpoch);
        markAlive();
        break;
      case 'p':
        bus.set(message.k, message.v);
        markAlive();
        break;
      case 'clock':
        applyClock(message.vt, message.anchorEpoch);
        markAlive();
        break;
      case 'bye':
        clearTimeout(idleTimer);
        drop();
        break;
      default:
        break; // 'req' — another mirror asking; not ours to answer.
    }
  };

  const sayBye = (): void => port.postMessage({ t: 'bye' });
  const offPageHide = onPageHide(sayBye);
  port.postMessage({ t: 'req' });

  return () => {
    clearTimeout(idleTimer);
    offPageHide();
    sayBye();
    port.onmessage = null;
    port.close();
  };
}
